// src/components/imagery/ExploreTimeStrip.jsx
//
// Explore's time strip (footprint restyle S-e slice 4a): ImageryStrip, the
// chips built by layers/imagery/exploreRows.js, replacing TimeSlider and its
// Sentinel-2 lane. This file wires the store and owns Play's timer and the
// message line; exploreRows.js says what is drawn.
//
// The actions, at the first captures row's end (DB's answers, 2026-11-03):
//   Play     steps the cursor chip to chip over the dates covering the map
//            center, every 1.5 s; the playing chip is ringed and the button is
//            a Pause. Never disabled: with every layer pinned the row says "All
//            layers are pinned. Unpin one to play."; with fewer than two
//            covering dates it says that. With no layer on, Play puts one on
//            that follows the dates, as a chip click does.
//   Compare  enters compare on the pair within one series (exploreRows.js
//            comparePairFor), marked A and B; with none, "No other <series>
//            date covers this view." It reads "Exit compare" while comparing.
//            With nothing on, it first puts on a layer that follows the cursor,
//            so leaving compare leaves the capture on.
//   Latest   the newest date covering the center.
// The message line clears on the next action (a chip, a button).
//
// Coverage (S-e5, DB 2026-11-03) is the GET /imagery/entitled answer's
// (MapView's own, `entitled` and `entitledStatus`), through
// layers/imagery/entitledCoverage.js, the logic SitePlanner's strip shares:
// the series' geometries are replaced by their entitled areas before anything
// reads them, so the chips, Latest, Play and Compare's pair all follow it, and
// nothing is dimmed before it answers. A covered date the map (`zoom`) is
// zoomed out past is marked zoom in; its click also calls onZoomTo(minzoom).
//
// A chip moves the one cursor; if nothing is rendering it also puts on one
// layer that follows the cursor (TimeSlider's rule: at most one addLayer, never
// a removeLayer or a reorder). Sentinel-2's chips move the same cursor.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Pause, Play } from "lucide-react";
import ImageryStrip from "./TimeStrip/ImageryStrip";
import { useImageryStackStore } from "../../stores/useImageryStackStore";
import { isFollowingCursor } from "../../layers/imagery/effectiveDate";
import { entitledAreas, withEntitledAreas } from "../../layers/imagery/entitledCoverage";
import {
  ALL_PINNED_TEXT, PLAY_INTERVAL_MS, REFERENCE_ROW_ID, captureSeries, noPairText, comparePairFor, exploreRows, latestCovered, playableDates, statusSentence,
} from "../../layers/imagery/exploreRows";

const BUTTON = "shrink-0 rounded border px-1.5 py-0.5 text-[11px] leading-[14px] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-action";
const BUTTON_IDLE = "border-bg-border text-fg-secondary hover:border-accent-action/60 hover:text-accent-action";
const BUTTON_ON = "border-accent-action/60 text-accent-action";
const BUTTON_OFF = "border-bg-border/60 text-fg-secondary/50 cursor-not-allowed";

/**
 * @param {object} props
 * @param {[number,number]|null} props.center  the map center [lng, lat]: coverage is decided there
 * @param {number|null} [props.zoom]  the map's zoom (the zoom-in mark)
 * @param {{features: object[], reference?: object[]}|null} [props.entitled]  GET /imagery/entitled's answer
 * @param {"loading"|"ok"|"error"} [props.entitledStatus]
 * @param {(zoom: number) => void} [props.onZoomTo]  a zoom-in chip's click: the map to that zoom, center kept
 */
export default function ExploreTimeStrip({ center = null, zoom = null, entitled = null, entitledStatus = "loading", onZoomTo = null }) {
  const catalog = useImageryStackStore((s) => s.catalog);
  const stack = useImageryStackStore((s) => s.stack);
  const cursor = useImageryStackStore((s) => s.cursor);
  const compareMode = useImageryStackStore((s) => s.compareMode);
  const getRenderSpecs = useImageryStackStore((s) => s.getRenderSpecs);
  const setGlobalCursor = useImageryStackStore((s) => s.setGlobalCursor);
  const addLayer = useImageryStackStore((s) => s.addLayer);
  const enterCompareForDates = useImageryStackStore((s) => s.enterCompareForDates);
  const exitCompareMode = useImageryStackStore((s) => s.exitCompareMode);

  const [playing, setPlaying] = useState(false);
  const [message, setMessage] = useState(null);
  const cursorDate = cursor.global_date;

  const areas = useMemo(() => entitledAreas(entitled, entitledStatus), [entitled, entitledStatus]);
  const seriesList = useMemo(() => withEntitledAreas(captureSeries(catalog, stack), areas), [catalog, stack, areas]);
  // Sentinel-2 the same, from the answer's `reference` areas (S-e5 check 7: an aoi grant's cells, not the composite's footprint).
  const referenceList = useMemo(() => withEntitledAreas(catalog?.reference ?? [], areas), [catalog, areas]);
  const hasFollower = useMemo(() => stack.some(isFollowingCursor), [stack]);
  const playable = useMemo(() => playableDates(seriesList, center), [seriesList, center]);
  const latest = useMemo(() => latestCovered(seriesList, center), [seriesList, center]);
  const rendering = useMemo(() => {
    const visible = getRenderSpecs().filter((sp) => sp.visible && sp.dataset_key);
    return { any: visible.length > 0, dates: new Set(visible.map((sp) => sp.effective_date).filter(Boolean)) };
    // stack, cursor and catalog are what resolveImageryStack reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRenderSpecs, stack, cursor, catalog]);

  // Play: one step per interval over the covering dates. A cursor on a date
  // that does not cover starts from the first that does ((-1 + 1) % n).
  useEffect(() => {
    if (!playing || playable.length < 2) return undefined;
    const id = setInterval(() => {
      const cur = useImageryStackStore.getState().cursor.global_date;
      setGlobalCursor(playable[(playable.indexOf(cur) + 1) % playable.length]);
    }, PLAY_INTERVAL_MS);
    return () => clearInterval(id);
  }, [playing, playable, setGlobalCursor]);

  // Stops by itself when it has nowhere to go: a pan to where one date covers,
  // or the last following layer pinned.
  useEffect(() => {
    if (playing && (!hasFollower || playable.length < 2)) setPlaying(false);
  }, [playing, hasFollower, playable.length]);

  // A date: the cursor first, so addLayer resolves against it; a layer only if nothing renders.
  const selectDate = useCallback((date, seriesKey) => {
    setGlobalCursor(date);
    if (!rendering.any && seriesKey) addLayer(seriesKey, { time_mode: "global" });
  }, [setGlobalCursor, addLayer, rendering.any]);

  const onChip = useCallback((rowId, key, chip) => {
    setMessage(null);
    const [prefix, date] = [key.slice(0, key.lastIndexOf("@")), key.slice(key.lastIndexOf("@") + 1)];
    if (rowId === REFERENCE_ROW_ID) setGlobalCursor(date);
    else selectDate(date, prefix);
    if (Number.isInteger(chip?.zoomIn)) onZoomTo?.(chip.zoomIn);
  }, [selectDate, setGlobalCursor, onZoomTo]);

  const onPlay = () => {
    setMessage(null);
    if (playing) { setPlaying(false); return; }
    if (stack.length && !hasFollower) { setMessage(ALL_PINNED_TEXT); return; }
    if (playable.length === 0) { setMessage("No date covers this view."); return; }
    if (playable.length === 1) { setMessage("Only one date covers this view."); return; }
    if (!stack.length) {
      const start = playable.includes(cursorDate) ? cursorDate : playable[0];
      const series = seriesList.find((s) => s.acquisitions?.some((a) => a.acquisition_date === start)) ?? seriesList[0];
      selectDate(start, series.series_key);
    }
    setPlaying(true);
  };

  const onCompare = () => {
    setMessage(null);
    if (compareMode.enabled) { exitCompareMode(); return; }
    const found = comparePairFor(seriesList, cursorDate, center);
    if (!found?.pair) {
      setMessage(noPairText(found?.series?.display_name ?? "imagery"));
      return;
    }
    // With nothing on, one layer that follows the cursor goes on first (Play's
    // and a chip's rule), on the pair's date at the cursor (else B): compare
    // takes away only what it created, so leaving it leaves that capture on.
    if (!rendering.any) {
      const { seriesKey, a, b } = found.pair;
      selectDate([a, b].includes(cursorDate) ? cursorDate : b, seriesKey);
    }
    enterCompareForDates(found.pair.seriesKey, found.pair.a, found.pair.b);
  };

  const isLatest = Boolean(latest.date) && cursorDate === latest.date;
  const onLatest = () => {
    setMessage(null);
    if (latest.date) selectDate(latest.date, latest.seriesKey);
  };

  const end = seriesList.length ? (
    <>
      <button type="button" onClick={onPlay} aria-label={playing ? "Pause" : "Play"}
        title={playing ? "Pause" : "Play through the dates covering the map center"}
        className={`${BUTTON} ${playing ? BUTTON_ON : BUTTON_IDLE} flex items-center`} data-testid="timestrip-play">
        {playing ? <Pause size={11} strokeWidth={2.5} /> : <Play size={11} strokeWidth={2.5} fill="currentColor" />}
      </button>
      <button type="button" onClick={onCompare} title={compareMode.enabled ? "Exit compare" : "Compare the selected date with the one before it"}
        className={`${BUTTON} ${compareMode.enabled ? BUTTON_ON : BUTTON_IDLE}`} data-testid="timestrip-compare-button">
        {compareMode.enabled ? "Exit compare" : "Compare"}
      </button>
      <button type="button" onClick={onLatest} disabled={!latest.date || isLatest}
        title="Return to the most recent imagery covering the map center"
        className={`${BUTTON} ${!latest.date || isLatest ? BUTTON_OFF : BUTTON_IDLE}`} data-testid="timestrip-latest">
        Latest
      </button>
    </>
  ) : null;

  const rows = exploreRows({ seriesList, referenceList, catalog, stack, cursor, compareMode, center, zoom, playing, end, message });
  if (!rows) return null;
  const announce = seriesList.length
    ? statusSentence({ seriesList, cursorDate, center, latestDate: latest.date, rendering })
    : null;
  return <ImageryStrip rows={rows} onChip={onChip} ariaLabel="Imagery dates" announce={announce} />;
}
